import { FormInstance } from 'antd/es/form';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useState, useEffect, ChangeEvent } from 'react';

import { ProviderCompany, Inspector } from '@/services/types/user.types';
import { useErrorHandler } from '@/shared/hooks/useErrorHandler';
import { providerCompanyService } from '@/services/providerCompany.service';
import { userService } from '@/services/user.service';
import { usePermissions } from '@/shared/hooks/usePermissions';

interface UseAssignFormProps {
  form: FormInstance;
  isInitialAssignment?: boolean;
}

export function useAssignForm({ form, isInitialAssignment = false }: UseAssignFormProps) {
  const [providers, setProviders] = useState<ProviderCompany[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<ProviderCompany[]>([]);
  const [inspectors, setInspectors] = useState<Inspector[]>([]);
  const [filteredInspectors, setFilteredInspectors] = useState<Inspector[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyQualified, setShowOnlyQualified] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState<string>(isInitialAssignment ? 'provider' : '');
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [isLoadingInspectors, setIsLoadingInspectors] = useState(false);

  const { handleError } = useErrorHandler();
  const { isProviderUser } = usePermissions();

  const destinationOptions = isProviderUser() ? [
    { value: 'internal', label: 'Interno', disabled: true },
  ] : [
    { value: 'provider', label: 'Prestadora', disabled: false },
    { value: 'inspector', label: 'Inspetor', disabled: isInitialAssignment },
    { value: 'internal', label: 'Interno', disabled: true },
  ];

  const fetchProviders = async () => {
    setIsLoadingProviders(true);
    try {
      const response = await providerCompanyService.getAllProviders();
      setProviders(response);
      setFilteredProviders(response);
    } catch (error) {
      handleError(error, 'Erro ao carregar prestadoras');
    } finally {
      setIsLoadingProviders(false);
    }
  };

  const fetchInspectors = async () => {
    setIsLoadingInspectors(true);
    try {
      const response = await userService.getInspectorsWithDirectAssignment({
        page: 1,
        size: 100,
      });
      setInspectors(response);
      setFilteredInspectors(response);
    } catch (error) {
      handleError(error, 'Erro ao carregar inspetores');
    } finally {
      setIsLoadingInspectors(false);
    }
  };

  useEffect(() => {
    // Se for direcionamento inicial, seta o valor do formulário como "provider"
    if (isInitialAssignment) {
      form.setFieldsValue({ destination: 'provider' });
    }
  }, [isInitialAssignment, form]);

  useEffect(() => {
    if (selectedDestination === 'provider') {
      fetchProviders();
    } else if (selectedDestination === 'inspector') {
      fetchInspectors();
    }
  }, [selectedDestination]);

  useEffect(() => {
    if (selectedDestination === 'provider') {
      let filtered = [...providers];

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(
          (provider) =>
            provider.name.toLowerCase().includes(term) ||
            provider.cnpj.toLowerCase().includes(term) ||
            provider.shortName?.toLowerCase().includes(term)
        );
      }

      if (showOnlyQualified) {
        filtered = filtered.filter((provider) => provider.allowsDemandReceiving);
      }

      setFilteredProviders(filtered);
    } else if (selectedDestination === 'inspector') {
      let filtered = [...inspectors];

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(
          (inspector) =>
            inspector.name.toLowerCase().includes(term) ||
            inspector.cpf.toLowerCase().includes(term)
        );
      }

      if (showOnlyQualified) {
        filtered = filtered.filter((inspector) => inspector.allowsDemandReceiving);
      }

      setFilteredInspectors(filtered);
    }
  }, [searchTerm, showOnlyQualified, providers, inspectors, selectedDestination]);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleQualifiedToggle = (e: CheckboxChangeEvent) => {
    setShowOnlyQualified(e.target.checked);
  };

  const handleDestinationChange = (value: string) => {
    setSelectedDestination(value);
    setSearchTerm('');
    setShowOnlyQualified(false);
  };

  return {
    destinationOptions,
    filteredProviders,
    filteredInspectors,
    searchTerm,
    showOnlyQualified,
    selectedDestination,
    isLoadingProviders,
    isLoadingInspectors,
    handleSearchChange,
    handleQualifiedToggle,
    handleDestinationChange,
  };
}
